import React, { useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { SchoolOverview } from '../SchoolOverview'
import { SchoolVideos } from '../SchoolVideos'
import { SchoolStatistics } from '../SchoolStatistics/SchoolStatistics'
import { SchoolAbout } from '../SchoolAbout'
import { SchoolLocation } from '../SchoolLocation'
import { SCREEN_SIZE, STYLES } from '../../static/style'
import { SchoolFeeDetails } from '../SchoolFeeDetails'
import { NearbySchoolsSection } from '../NearbySchoolsSection'
import { SchoolSocialSection } from '../Social/SchoolSocialSection'
import { SchoolListingGroups } from '../SchoolListingGroups'
import { SchoolDetailsSection } from './SchoolDetailsSection'
import {
  SchoolDetailsSectionContent,
  SchoolDetailsSectionTitle,
} from '../School/SchoolDetailsSectionTitle'
import { ScreenSizeHelper } from '../../helpers/ScreenSizeHelper'
import { School } from '../props/School.types'

const StyledSchoolDetails = styled.div`
  ${({ isFeatured }: { isFeatured: boolean }) =>
    isFeatured &&
    `
    border: 2px solid ${STYLES.PRIMARY_COLOR};
  `}
  ${SCREEN_SIZE.MD} {
    border: 0;
  }
`

const StyledSchoolFeeDetails = styled(SchoolFeeDetails)`
  width: 100%;
  padding: ${STYLES.SCHOOL_DETAILS_SECTION_GUTTER};
`

const SchoolDetailsContent = styled.div`
  padding: 0.5rem 0;
  background-color: ${STYLES.COLOR_MERCURY_GRAY};
`

const NearbySchoolDetailsSection = styled(SchoolDetailsSection)`
  background-color: ${STYLES.COLOR_LIGHT_OUTER_SPACE_GRAY};
  padding: 2rem 0.5rem;
`

interface ISchoolDetailsProps {
  school: School
}

const SchoolDetails: React.FC<ISchoolDetailsProps> = ({ school }) => {
  const [isCommunityLoadSuccess, setIsCommunityLoadSuccess] =
    useState<boolean>(false)
  const screenSize = useSelector(({ screenSize: sz }) => sz.size)
  if (!school) {
    return null
  }
  const {
    about,
    location,
    results,
    youtubeVideoIds,
    fee,
    nearbySchools,
    listings,
    contact,
    isFeatured,
  } = school
  const isMobileScreenSize = ScreenSizeHelper.getScreenSizeValueFromObject(
    screenSize,
    {
      default: true,
      MD: false,
    }
  )
  return (
    <StyledSchoolDetails isFeatured={isFeatured}>
      <SchoolOverview school={school} />
      {youtubeVideoIds && youtubeVideoIds.length > 0 && (
        <SchoolVideos youtubeVideoIds={school.youtubeVideoIds} />
      )}
      {results && <SchoolStatistics results={results} />}
      <SchoolDetailsContent>
        {(about || fee || (location && isMobileScreenSize)) && (
          <SchoolDetailsSection>
            <SchoolDetailsSectionTitle>
              School Information
            </SchoolDetailsSectionTitle>
            <SchoolDetailsSectionContent>
              {about && <SchoolAbout about={about} />}
              {fee && <StyledSchoolFeeDetails fee={fee} />}
              {location && <SchoolLocation location={location} />}
            </SchoolDetailsSectionContent>
          </SchoolDetailsSection>
        )}
        {location.coords && listings && (
          <SchoolDetailsSection>
            <SchoolDetailsSectionTitle>Activities</SchoolDetailsSectionTitle>
            <SchoolDetailsSectionContent>
              <SchoolListingGroups
                schoolCoords={location.coords}
                listings={listings}
              />
            </SchoolDetailsSectionContent>
          </SchoolDetailsSection>
        )}
        <SchoolDetailsSection isShow={isCommunityLoadSuccess}>
          <SchoolDetailsSectionTitle>Community</SchoolDetailsSectionTitle>
          <SchoolSocialSection
            facebook={contact?.facebook}
            twitter={contact?.twitter}
            onLoadSuccess={() => setIsCommunityLoadSuccess(true)}
          />
        </SchoolDetailsSection>
      </SchoolDetailsContent>
      {nearbySchools && (
        <NearbySchoolDetailsSection>
          <SchoolDetailsSectionContent>
            <NearbySchoolsSection schools={nearbySchools} />
          </SchoolDetailsSectionContent>
        </NearbySchoolDetailsSection>
      )}
    </StyledSchoolDetails>
  )
}

export { SchoolDetails }
